import React from 'react';
import {
  ActionIcon,
  ActionIconDropdown,
  Badge,
  DetailStack,
  Dropdown,
  FontWeight, HStack, ICON_EYE_OUTLINE, Message, Text, UserPreview,
  UserPreviewSkeleton,
  SimpleTable,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import { Invoice } from '../../../providers/api/dtos';
import ViewPropertyPopoverPreview from '../../../property/containers/view-property-popover-preview/ViewPropertyPopoverPreview';
import useProperty from '../../../property/hooks/use-property/useProperty';
import useContact from '../../../contact/hooks/use-contact/useContact';
import useAssignmentById from '../../../assignment/hooks/use-assignment-by-id/useAssignmentById';
import PropertyAvatar from '../../../property/components/property-avatar/PropertyAvatar';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';
import ViewContactPopoverPreview from '../../../contact/containers/view-contact-popover-preview/ViewContactPopoverPreview';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import { invoiceTypeLabels } from '../../../labels/invoiceTypeLabels';
import { InvoiceStatus, ReferenceType } from '../../../domain/types';
import DateViewer from '../../../components/date-viewer/DateViewer';
import { getCriticalIfInThePast, getPaymentDateLabel } from '../utils';
import { goToDetailPage } from '../../../utils/navigate/utils';
import UpsertInvoiceAction from '../upsert-invoice-action/UpsertInvoiceAction';
import UpdateInvoiceStatus from '../update-invoice-status/UpdateInvoiceStatus';
import ManageInvoiceCommissionsAction from '../manage-invoice-commissions-action/ManageInvoiceCommissionsAction';
import DeleteInvoiceAction from '../delete-invoice-action/DeleteInvoiceAction';
import useCompany from '../../../company/hooks/use-company/useCompany';
import ViewCompanyPopoverPreview from '../../../company/containers/view-company-popover-preview/ViewCompanyPopoverPreview';
import CompanyAvatar from '../../../company/components/company-avatar/CompanyAvatar';
import InvoiceValuePopover from '../../components/invoice-value-popover/InvoiceValuePopover';
import { computeGrossValue } from '../../utils';
import * as styles from './ViewInvoiceTableRow.style';
import useRBAC from '../../../hooks/use-rbac/useRBAC';

export type InvoiceContextPage = 'HOLDER' | 'PROPERTY';

export interface ViewInvoiceTableRowProps {
  invoice: Invoice,
  context: InvoiceContextPage,
  canView?: boolean,
  canEdit?: boolean,
  onEdit?: (invoice: Invoice) => void,
}

const ViewInvoiceTableRow: React.FC<ViewInvoiceTableRowProps> = ({
  invoice,
  context,
  canView = false,
  canEdit = false,
  onEdit,
}) => {
  const { data: assignment, isLoading: isAssignmentLoading, error: assignmentError } = useAssignmentById(invoice.assignmentId);
  const { data: property, isLoading: isPropertyLoading, error: propertyError } = useProperty(assignment?.propertyId);
  const { data: contact, isLoading: isContactLoading, error: contactError } = useContact(invoice.reference.type === ReferenceType.CONTACT ? invoice.reference.id : undefined);
  const { data: company, isLoading: isCompanyLoading, error: companyError } = useCompany(invoice.reference.type === ReferenceType.COMPANY ? invoice.reference.id : undefined);
  const { userIsAdmin, userIsFinance } = useRBAC();

  const paymentDateLabel = React.useMemo(() => getPaymentDateLabel(invoice.status), [invoice.status]);
  const grossValue = React.useMemo(() => computeGrossValue(invoice.value, invoice.vat), [invoice.value, invoice.vat]);
  const amountLabel = grossValue !== undefined ? 'Importo' : 'Importo (netto)';
  const displayAmount = grossValue ?? invoice.value;

  const disabledInvoiceFields = React.useMemo(() => ({
    property: context === 'PROPERTY',
    contact: context === 'HOLDER',
    company: context === 'HOLDER',
  }), [context]);

  const goToInvoiceDetailPage = React.useCallback(() => {
    goToDetailPage(invoice.id!, ReferenceType.INVOICE);
  }, [invoice.id]);

  return (
    <SimpleTable.Row>
      <SimpleTable.Cell aria-label="Informazioni sull'entità" width="12rem">
        {(isAssignmentLoading || isPropertyLoading || isContactLoading || isCompanyLoading) && (
          <UserPreviewSkeleton size="S" />
        )}

        {(assignmentError || propertyError || contactError || companyError) && (
          <Message
            type="critical"
            message="Errore"
            aria-label="Errore di caricamento"
          />
        )}

        {context === 'HOLDER' && property && (
          <ViewPropertyPopoverPreview
            property={property}
            trigger={(
              <HStack gap={100} wrap="nowrap">
                <PropertyAvatar size="S" propertyType={property.propertyType} />
                <Text.BodySmall
                  fontWeight={FontWeight.MEDIUM}
                  css={setVerticalEllipsis(2)}
                >
                  {property.geo?.normalizedAddress}
                </Text.BodySmall>
              </HStack>
            )}
          />
        )}

        {context === 'PROPERTY' && contact && (
          <ViewContactPopoverPreview
            contact={contact}
            trigger={(
              // eslint-disable-next-line react/no-unknown-property
              <div css={styles.userWrapper}>
                <UserPreview
                  name={contact.name || NO_VALUE_SYMBOL}
                  size="S"
                >
                  {contact.phoneNumber || NO_VALUE_SYMBOL}
                </UserPreview>
              </div>
            )}
          />
        )}

        {context === 'PROPERTY' && company && (
          <ViewCompanyPopoverPreview
            company={company}
            trigger={(
              <HStack gap={100} wrap="nowrap">
                <CompanyAvatar size="S" />
                <Text.BodySmall
                  fontWeight={FontWeight.MEDIUM}
                  css={setVerticalEllipsis(2)}
                >
                  {company.name}
                </Text.BodySmall>
              </HStack>
            )}
          />
        )}

      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Tipo del pagamento">
        <Badge
          label={invoiceTypeLabels[invoice.type]}
          color="neutral"
          size="XS"
          emphasis="low"
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Stato del pagamento">
        <UpdateInvoiceStatus
          invoice={invoice}
          canEditType={canEdit}
          emphasis="low"
          size="XS"
          onSuccess={onEdit}
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Importo del pagamento">
        <DetailStack label={amountLabel}>
          <InvoiceValuePopover
            invoice={invoice}
            trigger={(
              <Text.Caption
                fontWeight={FontWeight.REGULAR}
                whitespace="nowrap"
                lineHeight="1"
              >
                {formatEuro(displayAmount)}
              </Text.Caption>
            )}
          />
        </DetailStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Data del pagamento">
        <DetailStack label={paymentDateLabel}>
          <DateViewer stringDate={invoice.paymentDate} highlightWhen={invoice.status !== InvoiceStatus.COLLECTED ? getCriticalIfInThePast : undefined} />
        </DetailStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell withDivider={invoice.status !== InvoiceStatus.NOT_PAID}>
        {[InvoiceStatus.PAID, InvoiceStatus.COLLECTED].includes(invoice.status) && (
          <DetailStack
            label="Rif. Fattura"
            aria-label="Identificato riferimento fattura"
          >
            {invoice.invoiceId}
          </DetailStack>
        )}

        {[InvoiceStatus.PROFORMA, InvoiceStatus.NOT_PAID].includes(invoice.status) && (
          <DetailStack
            label="Rif. Proforma"
            aria-label="Identificato riferimento proforma"
          >
            {invoice.proformaInvoiceId}
          </DetailStack>
        )}
      </SimpleTable.Cell>

      <SimpleTable.Cell align="right">
        <HStack gap={75} wrap="nowrap">
          {canView && (
            <ActionIcon
              label="Visualizza"
              aria-label="Visualizza dettagli pagamento"
              icon={{ path: ICON_EYE_OUTLINE }}
              size="S"
              onClick={goToInvoiceDetailPage}
            />
          )}
          {canEdit && (
            <ActionIconDropdown
              label="Azioni rapide sul pagamento"
              aria-label="Dropdown di azioni rapide sul pagamento"
              size="S"
              emphasis="low"
            >
              {invoice.status !== InvoiceStatus.NOT_PAID && (
                <UpsertInvoiceAction
                  invoice={invoice}
                  onSuccess={onEdit}
                  disabledFields={disabledInvoiceFields}
                >
                  {({ openUpsertInvoiceModal }) => (
                    <Dropdown.Option
                      label="Modifica pagamento"
                      aria-label="Modifica pagamento"
                      onClick={openUpsertInvoiceModal}
                    />
                  )}
                </UpsertInvoiceAction>
              )}
              {[InvoiceStatus.DRAFT, InvoiceStatus.PROFORMA].includes(invoice.status) && (
                <DeleteInvoiceAction
                  invoiceId={invoice.id!}
                  onSuccess={() => onEdit?.(invoice)}
                >
                  {({ onOpenModal }) => (
                    <Dropdown.Option
                      label="Elimina pagamento"
                      aria-label="Elimina pagamento"
                      onClick={onOpenModal}
                    />
                  )}
                </DeleteInvoiceAction>
              )}
              {(userIsAdmin || userIsFinance) && invoice.status !== InvoiceStatus.NOT_PAID && (
                <ManageInvoiceCommissionsAction
                  invoice={invoice}
                  onSuccess={onEdit}
                >
                  {({ manageCommissions }) => (
                    <Dropdown.Option
                      label="Gestisci commissioni"
                      aria-label="Gestisci commissioni"
                      onClick={manageCommissions}
                    />
                  )}
                </ManageInvoiceCommissionsAction>
              )}
            </ActionIconDropdown>
          )}
        </HStack>
      </SimpleTable.Cell>
    </SimpleTable.Row>
  );
};

export default ViewInvoiceTableRow;
